import {makeAutoObservable, runInAction} from "mobx";
import {rootContainer, TYPE} from "../../../services/container/root";
import ParentChildService, {IStoreFamily} from "../../../services/api/parent-child-service";
import {IShop} from "../../../models/User";
import { UserMasterListStore } from "./user-master-list-store";

class ParentChildStore {
    private _isLoading: boolean = false;
    private _isSaving: boolean = false;
    private _model: IStoreFamily = {} as IStoreFamily;
    private _childrenShopsToRemoveIds: Array<number> = [];
    private _childrenShopsToAddList: IShop[] = [];
    private _selectedUserId: number = 0;
    private _userMasterListStore: UserMasterListStore;

    constructor(userMasterListStore: UserMasterListStore) {
        this._userMasterListStore = userMasterListStore;
        makeAutoObservable(this);
    }

    private setLoading(isLoading: boolean) { this._isLoading = isLoading; }
    private get service(): ParentChildService { return rootContainer.get<ParentChildService>(TYPE.ParentChildService); }
    public get isLoading(): boolean { return this._isLoading; }
    public get isSaving(): boolean { return this._isSaving; }

    public getChildren() { return this._model.children; }
    public getSelectedUserPlan() { return this._model.plan; }

    public get childrenToRemove(): Array<number> {
        return this._childrenShopsToRemoveIds;
    }

    public getParent() {
        return this._model?.parent;
    }

    public get childrenToAdd(): Array<number> {
        return this._childrenShopsToAddList.map(child => child.id);
    }

    public isChildOnRemove(childId: number): boolean {
        return this._childrenShopsToRemoveIds.includes(childId);
    }

    public get selectedUserIsChild(): boolean {
        return !!this._model?.children?.find(child => child.id === this._selectedUserId);
    }

    public get selectedUserId(): number {
        return  this._selectedUserId;
    }

    public setChildrenToAdd(list: IShop[]) {
        this._childrenShopsToAddList = list;
    }

    public getChildrenToAdd(): IShop[] {
        return this._childrenShopsToAddList;
    }

    public removeChild(childId: number) {
        this._childrenShopsToRemoveIds.push(childId);
    };

    public rollbackChildRemove(rollbackChildId: number) {
        this._childrenShopsToRemoveIds = this._childrenShopsToRemoveIds.filter(childId => childId !== rollbackChildId);
    }

    public hasChanged(): boolean {
        return this._childrenShopsToAddList.length > 0 || this._childrenShopsToRemoveIds.length > 0;
    }

    public async confirmChanges() {
        this._isSaving = true;
        try {
            let storeFamily = await this.service.sendChildrenChanges(
                this.selectedUserId,
                this._model.parent.id,
                this._childrenShopsToRemoveIds,
                this._childrenShopsToAddList.map(child => child.id)
            );
            runInAction(() => {
                this._model = storeFamily;
                this._userMasterListStore.reloadByIds(this._childrenShopsToAddList.map(child => child.id).concat(this._childrenShopsToRemoveIds));
                this._childrenShopsToRemoveIds = [];
                this._childrenShopsToAddList = [];
                this._isSaving = false;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    async load(userId: number): Promise<void> {
        this.setLoading(true);
        try {
            this._selectedUserId = userId;
            const storeFamily = await this.service.getUserWithParentChildRelations(userId);
            runInAction(() => {
                this._model = storeFamily;
                this._childrenShopsToRemoveIds = [];
                this._childrenShopsToAddList = [];
                this.setLoading(false);
            });
        }
        catch (error) {
            runInAction(() => {
                this.setLoading(false);
            });
        }
    }
}

export default ParentChildStore;